import React, { useState, useRef, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Paper from '@mui/material/Paper';
import Link from '@mui/material/Link';
import { useTranslation } from 'react-i18next';
import ShowMoreContent from '../../../base/ShowMoreContent';
import { MuiMarkdown, getOverrides } from 'mui-markdown';
import ButtonBase from '@mui/material/ButtonBase';

import { CardContainer } from './CardContainer';

import IconText from '../../../base/IconText';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useDetailsViewContext } from '../DetailsViewContext';

/**
 * Card component displaying resource description and URL with original styling
 *
 * @param {Object} props
 * @param {number|null} props.allocatedHeight - Height allocated by parent for this card
 * @param {boolean} props.autoExpand - Whether to auto-expand and hide ShowMoreCard toggle
 * @param {boolean} props.forceCollapsed - When true, use fixed collapsed height (for mobile)
 * @param {Function} props.onDimensionsChange - Callback to report min/max dimensions
 */
export default function ResourceCard({
  allocatedHeight,
  autoExpand = false,
  forceCollapsed = false,
  onDimensionsChange,
}) {
  const { t } = useTranslation();
  // Fixed height for mobile collapsed state (~3-4 lines of text)
  const mobileCollapsedHeight = 80;

  const [descriptionExpanded, expandDescription] = useState(
    autoExpand && !forceCollapsed,
  );
  // Track if collapse animation is complete - only then apply height constraint
  const [collapseAnimationComplete, setCollapseAnimationComplete] =
    useState(true);

  const { publicData } = useDetailsViewContext();

  // Refs for dimension measurement
  const fixedContentRef = useRef(null);
  const maxContentRef = useRef(null);

  // State for measured fixed content height (name + url link)
  const [fixedContentHeight, setFixedContentHeight] = useState(0);

  // Sync expansion with autoExpand/forceCollapsed props
  useEffect(() => {
    if (forceCollapsed) {
      // When forceCollapsed is enabled, ensure collapsed state
      expandDescription(false);
    } else {
      // When forceCollapsed is disabled, sync with autoExpand
      expandDescription(autoExpand);
      if (autoExpand) {
        // When switching to autoExpand, ensure height constraint is released
        setCollapseAnimationComplete(true);
      }
    }
  }, [autoExpand, forceCollapsed]);

  const toggleDescriptionExpansion = useCallback(() => {
    if (!autoExpand) {
      expandDescription((prev) => {
        const newExpanded = !prev;
        if (!newExpanded) {
          // Starting collapse - animation not yet complete
          setCollapseAnimationComplete(false);
        }
        return newExpanded;
      });
    }
  }, [autoExpand]);

  // Called when ShowMoreCard animation finishes.
  // Only tracks collapse animation completion since we need to defer
  // height constraint until collapse animation finishes.
  // Expand animation doesn't need tracking - height constraint is
  // removed immediately when expansion starts.
  const handleAnimationComplete = useCallback((isExpanded) => {
    if (!isExpanded) {
      // Collapse animation finished - now safe to apply height constraint
      setCollapseAnimationComplete(true);
    }
  }, []);

  // Measure fixed content height (name + url link)
  useEffect(() => {
    const measureFixedContent = () => {
      if (fixedContentRef.current) {
        setFixedContentHeight(fixedContentRef.current.scrollHeight || 0);
      }
    };

    const rafId = requestAnimationFrame(measureFixedContent);

    const ro = new ResizeObserver(measureFixedContent);
    if (fixedContentRef.current) ro.observe(fixedContentRef.current);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [publicData?.resource]);

  // Measure and report dimensions for height distribution
  useEffect(() => {
    if (!onDimensionsChange) return;

    const fixedEl = fixedContentRef.current;
    const maxEl = maxContentRef.current;

    if (!fixedEl || !maxEl) return;

    const measureDimensions = () => {
      // Min height = fixed content only (ShowMoreCard will add to this)
      const minHeight = fixedEl?.scrollHeight || 0;
      const maxHeight = maxEl?.scrollHeight || 0;
      onDimensionsChange({ minHeight, maxHeight });
    };

    let rafId1;
    let rafId2;
    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(measureDimensions);
    });

    const ro = new ResizeObserver(measureDimensions);
    ro.observe(fixedEl);
    ro.observe(maxEl);

    return () => {
      if (rafId1) cancelAnimationFrame(rafId1);
      if (rafId2) cancelAnimationFrame(rafId2);
      ro.disconnect();
    };
  }, [onDimensionsChange, publicData?.resource]);

  if (!publicData?.resource?.description && !publicData?.resource?.url) {
    return null;
  }

  // Shared URL link content
  const renderUrlLink = () =>
    publicData?.resource?.url && (
      <Grid
        item
        xs={12}
        sx={{
          mt: 1,
        }}
      >
        <IconText icon={OpenInNewIcon}>
          <Link underline="none" href={publicData?.resource?.url}>
            {t('detailsView.additional_information')}
          </Link>
        </IconText>
      </Grid>
    );

  // Calculate maxHeight for ShowMoreCard
  // This is the remaining height after fixed content (name, url link, padding, spacing)
  // Note: We always pass maxHeight (even when expanded) so ShowMoreCard knows
  // whether to show the collapse button. ShowMoreCard handles displaying
  // full height when expanded.
  const calculateShowMoreMaxHeight = () => {
    // When forceCollapsed, use fixed mobile height regardless of other settings
    if (forceCollapsed) {
      return mobileCollapsedHeight;
    }

    if (!allocatedHeight || autoExpand) {
      return null; // No limit
    }

    // Card padding: p: { xs: 1, sm: 2 } = 8px or 16px on each side
    const cardPadding = 32; // 16px top + 16px bottom
    const showMoreSpacing = 8; // mt: 1 = 8px spacing before ShowMoreCard

    const availableForShowMore =
      allocatedHeight - fixedContentHeight - cardPadding - showMoreSpacing;

    return Math.max(0, availableForShowMore);
  };

  const showMoreMaxHeight = calculateShowMoreMaxHeight();

  return (
    <Grid id="resource-card" item container xs={12}>
      {/* Hidden measurement element for fixed content (url link) */}
      <Box
        ref={fixedContentRef}
        aria-hidden="true"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          right: 0,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        <CardContainer>{renderUrlLink()}</CardContainer>
      </Box>

      {/* Hidden measurement element for max content (everything expanded) */}
      <Box
        ref={maxContentRef}
        aria-hidden="true"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          right: 0,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        <CardContainer>
          {publicData?.resource?.description && (
            <Grid item xs={12}>
              <MuiMarkdown>{publicData.resource.description}</MuiMarkdown>
            </Grid>
          )}
          {renderUrlLink()}
        </CardContainer>
      </Box>

      {/* Actual visible content */}
      <CardContainer
        sx={{
          ...(allocatedHeight &&
            !descriptionExpanded &&
            collapseAnimationComplete && {
              maxHeight: `${allocatedHeight}px`,
              overflow: 'hidden',
            }),
        }}
      >
        {publicData?.resource?.description && (
          <Grid item xs={12}>
            {autoExpand ? (
              // Full content without ShowMoreContent
              <MuiMarkdown>{publicData.resource.description}</MuiMarkdown>
            ) : (
              <ShowMoreContent
                maxHeight={showMoreMaxHeight}
                expanded={descriptionExpanded}
                onToggle={toggleDescriptionExpansion}
                onAnimationComplete={handleAnimationComplete}
                buttonComponent={ButtonBase}
                buttonProps={{
                  buttonSx: {
                    pr: 1,
                    borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  },
                }}
              >
                <MuiMarkdown
                  overrides={{
                    ...getOverrides({}),
                    a: {
                      component: Link,
                      props: {
                        target: '_blank',
                      },
                    },
                  }}
                >
                  {publicData.resource.description}
                </MuiMarkdown>
              </ShowMoreContent>
            )}
          </Grid>
        )}
        {renderUrlLink()}
      </CardContainer>
    </Grid>
  );
}

export function ResourceCardSkeleton() {
  return (
    <Paper
      sx={{
        p: 2,
        border: '1px solid',
        borderRadius: (theme) => `${theme.shape.borderRadius}px`,
        borderColor: (theme) => theme.palette.divider,
      }}
    >
      <Skeleton variant="text" width="100%" height={20} />
      <Skeleton variant="text" width="90%" height={20} />
      <Skeleton variant="text" width="70%" height={20} />
    </Paper>
  );
}
